import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EstensaRestControllerType, EstensaRestListType, ISourceItemData, ISourceItemDataExtended } from '@se/models';
import { firstValueFrom } from 'rxjs';
import { ServerRootService } from '../utils/routing/serverroot.service';
import { NotificationService } from './notification.service';

export interface ICacheResultDict {
    ListEcg: Record<string, ISourceItemData[]>;
    List: Record<string, ISourceItemData[]>; //oggi si chiama listcontroller va rifattorizzato a list visit in sync con il backend
    ListCpReporting: Record<string, ISourceItemData[]>;
    ListWorkFlow: Record<string, ISourceItemData[]>;
    Visit: Record<string, ISourceItemData[]>;
    Worklist: Record<string, ISourceItemData[]>;
    Workflow: Record<string, ISourceItemData[]>;
    RadiologyCommon: Record<string, ISourceItemData[]>;
    CustomPageInternal: Record<string, ISourceItemData[]>;
    Report: Record<string, ISourceItemData[]>;
    Senology: Record<string, ISourceItemData[]>;
    Execution: Record<string, ISourceItemData[]>;
    ECG: Record<string, ISourceItemData[]>;
    ClinicalRepository: Record<string, ISourceItemData[]>;
}

export const LIST_TO_PRELOAD_ON_BOOT: [EstensaRestControllerType, EstensaRestListType][] = [
    ['List', EstensaRestListType.lsUsEquipment],
    ['ListWorkFlow', EstensaRestListType.lsSex],
    ['ListWorkFlow', EstensaRestListType.lsRace],
    ['ListWorkFlow', EstensaRestListType.lsCity],
    ['ListWorkFlow', EstensaRestListType.lsResidenceCity],
    ['ListWorkFlow', EstensaRestListType.lsCivilStatus],
    ['ListWorkFlow', EstensaRestListType.lsProfession],
    ['ListWorkFlow', EstensaRestListType.lsAddress],
];

@Injectable({
    providedIn: 'root',
})
export class ListDataSourceService extends ServerRootService {
    listCache: ICacheResultDict = {
        ListEcg: {},
        List: {},
        ListCpReporting: {},
        ListWorkFlow: {},
        Visit: {},
        Worklist: {},
        Workflow: {},
        RadiologyCommon: {},
        CustomPageInternal: {},
        Report: {},
        Senology: {},
        Execution: {},
        ECG: {},
        ClinicalRepository: {},
    };

    constructor(
        private httpClient: HttpClient,
        private notificationService: NotificationService,
    ) {
        super();
    }

    async get<T extends ISourceItemData>(
        controllerName: EstensaRestControllerType,
        methodName: string,
        params: string,
        forceResetCache = false,
        parser: ((data: T[]) => T[]) | null = null,
    ): Promise<T[]> {
        const methodAndParams = params ? `${methodName}?${params}` : methodName;
        if (!forceResetCache && this.listCache[controllerName][methodAndParams]?.length) {
            return this.listCache[controllerName][methodAndParams] as unknown as T[];
        }
        try {
            const result = await firstValueFrom(
                this.httpClient.get<T[]>(
                    `${this.getServerRoot()}/${controllerName}/${methodAndParams}`,
                    this.buildOptions(),
                ),
            );
            let data = result;
            if (parser) data = parser(result);
            this.listCache[controllerName][methodAndParams] = data;
            return data;
        } catch (err: any) {
            console.error(err);
            this.notificationService.error(err.message, `${controllerName}/${methodAndParams}`);
            this.listCache[controllerName][methodAndParams] = [];
            return [];
        }
    }

    async preloadLists() {
        for (const list of LIST_TO_PRELOAD_ON_BOOT) {
            const [controllerName, listName] = list;
            await this.getList(controllerName, listName);
        }
    }
    async getList<T extends ISourceItemDataExtended>(
        controllerName: EstensaRestControllerType,
        listName: EstensaRestListType,
        forceResetCache = false,
    ) {
        const method = 'GetListItems';
        const params = `lsName=${listName}`;
        return this.get<T>(controllerName, method, params, forceResetCache);
    }

    async getLSExam(forceResetCache = false) {
        return this.getList<ISourceItemDataExtended>('ListWorkFlow', EstensaRestListType.lsExam, forceResetCache);
    }

    async getLsUsMeasureExtension(forceResetCache = false) {
        return this.getList('List', EstensaRestListType.lsUsMeasureExtension, forceResetCache);
    }

    async getLsUsEquipment(forceResetCache = false) {
        return this.getList('List', EstensaRestListType.lsUsEquipment, forceResetCache);
    }

    async getLsSeverity(forceResetCache = false) {
        return this.getList('ListWorkFlow', EstensaRestListType.lsSeverity, forceResetCache);
    }

    async getLsRole(forceResetCache = false) {
        return this.getList('ListWorkFlow', EstensaRestListType.lsRole, forceResetCache);
    }

    async getLsSuspensionReason(forceResetCache = false) {
        return this.getList('ListWorkFlow', EstensaRestListType.lsSuspensionReason, forceResetCache);
    }

    async getLsDeletingReason(forceResetCache = false) {
        return this.getList('ListWorkFlow', EstensaRestListType.lsDeletingReason, forceResetCache);
    }

    async getEquipmentsForMandatoryChange(controller: EstensaRestControllerType, forceResetCache = false) {
        return this.get(controller, 'GetEquipmentsForMandatoryChange', '', forceResetCache);
    }

    async getCustomTemplates(controller: EstensaRestControllerType = 'ClinicalRepository', forceResetCache = false) {
        return await this.get<any>(controller, 'getCustomTemplates', '', forceResetCache, (data) =>
            data.map((template) => ({
                guid: template.name,
                description: template.description,
            })),
        );
    }

    async getLsSex(forceResetCache = false) {
        return this.getList('ListWorkFlow', EstensaRestListType.lsSex, forceResetCache);
    }

    async getLsRace(forceResetCache = false) {
        return this.getList('ListWorkFlow', EstensaRestListType.lsRace, forceResetCache);
    }

    async getLsCity(forceResetCache = false) {
        return this.getList('ListWorkFlow', EstensaRestListType.lsCity, forceResetCache);
    }

    async getLsResidenceCity(forceResetCache = false) {
        return this.getList('ListWorkFlow', EstensaRestListType.lsResidenceCity, forceResetCache);
    }

    async getLsCivilStatus(forceResetCache = false) {
        return this.getList('ListWorkFlow', EstensaRestListType.lsCivilStatus, forceResetCache);
    }

    async getLsAddress(forceResetCache = false) {
        return this.getList('ListWorkFlow', EstensaRestListType.lsAddress, forceResetCache);
    }

    async getLsProfession(forceResetCache = false) {
        return this.getList('ListWorkFlow', EstensaRestListType.lsProfession, forceResetCache);
    }

    async getLsSource(forceResetCache = false) {
        return this.getList('ListWorkFlow', EstensaRestListType.lsSource, forceResetCache);
    }

    async getLsUsExam(forceResetCache = false) {
        return this.getList('ListWorkFlow', EstensaRestListType.lsUsExam, forceResetCache);
    }

    async getLsHistoricalCity(forceResetCache = false) {
        return this.getList('ListWorkFlow', EstensaRestListType.lsHistoricalCity, forceResetCache);
    }

    async getLsDeliveryDocumentationType(forceResetCache = false) {
        return this.getList('ListWorkFlow', EstensaRestListType.lsDeliveryDocumentationType, forceResetCache);
    }

    async getLsDoseClass(forceResetCache = false) {
        return this.getList('ListWorkFlow', EstensaRestListType.lsDoseClass, forceResetCache);
    }

    async getLsPredefinedTextGroup(forceResetCache = false) {
        return this.getList('ListWorkFlow', EstensaRestListType.lsPredefinedTextGroup, forceResetCache);
    }

    async getLsMinisterialCode(forceResetCache = false) {
        return this.getList('ListWorkFlow', EstensaRestListType.lsMinisterialCode, forceResetCache);
    }

    async getDynamicFields(forceResetCache = false) {
        return this.get('ListWorkFlow', 'GetDynamicFields', '', forceResetCache);
    }

    async getPatientHistorySources(forceResetCache = false) {
        return this.getList('ListWorkFlow', EstensaRestListType.lsReportHistorySource, forceResetCache);
    }
}
